import { MyReactions, PostReactionType, ReactionStats } from '__generated__/graphql/legacy/graphql';
import TrackingEvent, { TrackingProperty } from 'constants/trackingEvents';
import { EVENT_POST_TYPES, NUMBER_OF_REACTIONS } from 'utils/constants';
import { rawId } from 'utils/rawId';
import { failureToastMessage, genericToastAlert } from 'utils/toastHelpers';

import { useCallback, useState } from 'react';

import { useMutation } from '@apollo/client';

import { useCommunityContext } from 'components/CommunityContext';

import { TOGGLE_REACTION_ON_POST_MUTATION } from 'schema/Post/mutations';
import { TOGGLE_REACTION_ON_REPLY_MUTATION } from 'schema/Replies/mutations';

import { REACTIONS_SUMMARY_MAPPING } from '../../utils/reactionUtils';

type ReactionData = { count: number; reactors?: string };

const addReactionToStats = (
  stats: ReactionStats,
  reactionKey: string,
  currentReaction: ReactionData | undefined
): ReactionStats => {
  const currentCount = currentReaction?.count || 0;
  return {
    ...stats,
    [reactionKey]: { ...currentReaction, count: currentCount + 1 },
  };
};

const removeReactionFromStats = (
  stats: ReactionStats,
  reactionKey: string,
  currentReaction: ReactionData | undefined
): ReactionStats => {
  const currentCount = currentReaction?.count || 0;
  if (currentCount > 1) {
    return {
      ...stats,
      [reactionKey]: { ...currentReaction, count: currentCount - 1 },
    };
  }
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { [reactionKey]: _removed, ...rest } = stats;
  return rest;
};

const addToMyReactions = (
  myReactions: MyReactions,
  reactionType: PostReactionType
): MyReactions => {
  return [...myReactions, reactionType];
};

const removeFromMyReactions = (
  myReactions: MyReactions,
  reactionType: PostReactionType
): MyReactions => {
  const newReactions = [...myReactions];
  const index = newReactions.indexOf(reactionType);
  newReactions.splice(index, 1);
  return newReactions;
};

interface UseReactionHandlerProps {
  id: string;
  parentPostId?: string;
  parentReplyId?: string;
  initialReactionStats?: ReactionStats;
  initialMyReactions?: MyReactions;
}

export const useReactionHandler = ({
  id,
  parentPostId,
  parentReplyId,
  initialReactionStats = {},
  initialMyReactions = [],
}: UseReactionHandlerProps) => {
  const { trackEvent } = useCommunityContext();

  // Optimistic state management
  const [reactionStats, setReactionStats] = useState<ReactionStats>(initialReactionStats);
  const [myReactions, setMyReactions] = useState<MyReactions>(initialMyReactions);

  // API mutations
  const [toggleReactionOnPostMutation] = useMutation(TOGGLE_REACTION_ON_POST_MUTATION, {
    onError: () => {
      genericToastAlert();
    },
  });

  const [toggleReactionOnReplyMutation] = useMutation(TOGGLE_REACTION_ON_REPLY_MUTATION, {
    onError: () => {
      genericToastAlert();
    },
  });

  // Apply optimistic reaction toggle
  const applyOptimisticReaction = useCallback(
    (reactionType: PostReactionType) => {
      const reactionKey = Object.keys(REACTIONS_SUMMARY_MAPPING).find(
        (key) => REACTIONS_SUMMARY_MAPPING[key].graphqlEnum === reactionType
      );

      if (!reactionKey) {
        return;
      }

      // Update myReactions
      setMyReactions((prevMyReactions) => {
        const currentlyHasReaction = prevMyReactions.includes(reactionType);

        if (currentlyHasReaction) {
          return removeFromMyReactions(prevMyReactions, reactionType);
        }
        return addToMyReactions(prevMyReactions, reactionType);
      });

      // Update reactionStats
      setReactionStats((prevStats) => {
        const currentlyHasReaction = myReactions.includes(reactionType);
        const currentReaction = prevStats[reactionKey];

        if (currentlyHasReaction) {
          return removeReactionFromStats(prevStats, reactionKey, currentReaction);
        }
        return addReactionToStats(prevStats, reactionKey, currentReaction);
      });
    },
    [myReactions]
  );

  // Rollback optimistic reaction (called on error)
  const rollbackReaction = useCallback(
    (reactionType: PostReactionType) => {
      // Simply revert the last optimistic change by applying the same toggle again
      applyOptimisticReaction(reactionType);
    },
    [applyOptimisticReaction]
  );

  // Track reaction events
  const trackReactEvent = useCallback(
    ({ hasReacted, reactionType }: { hasReacted: boolean; reactionType: string }) => {
      let postType = parentReplyId ? EVENT_POST_TYPES.REPLY : EVENT_POST_TYPES.COMMENT;
      postType = parentPostId ? postType : EVENT_POST_TYPES.POST;
      const reactedAt = new Date();

      trackEvent(TrackingEvent.POST_REACTED, {
        [TrackingProperty.POST_ID]: rawId(id),
        [TrackingProperty.POST_ID_ENCODED]: id,
        [TrackingProperty.POST_TYPE]: postType,
        [TrackingProperty.INTERACTION_TYPE]: hasReacted ? 'Added Reaction' : 'Removed Reaction',
        [TrackingProperty.REACTION_TYPE_NAME]: reactionType,
        [TrackingProperty.REACTION_TIMESTAMP]: reactedAt.toISOString(),
      });
    },
    [trackEvent, id, parentPostId, parentReplyId]
  );

  // Handle API call for parent post reactions
  const handleParentPostReactionClick = useCallback(
    async (reactionType: PostReactionType) => {
      try {
        const { data: reactPostData, errors: reactPostError } = await toggleReactionOnPostMutation({
          variables: {
            id,
            reactionType,
            numberOfReactions: NUMBER_OF_REACTIONS,
            multipleReactionsEnabled: true,
          },
        });

        const userErrors = reactPostData?.toggleReactionOnPost?.userErrors;

        if (userErrors?.length || reactPostError) {
          // Handle user errors with specific messages and rollback optimistic update
          if (userErrors?.length) {
            const errorMessage = userErrors[0]?.message;
            if (errorMessage) {
              failureToastMessage(errorMessage);
            } else {
              genericToastAlert();
            }
          }
          // Rollback the optimistic update
          rollbackReaction(reactionType);
        } else {
          // Success - track the event
          const updatedPost = reactPostData?.toggleReactionOnPost?.post;
          const hasReacted = updatedPost?.myReactions?.includes(reactionType);

          trackReactEvent({
            hasReacted,
            reactionType,
          });
        }
      } catch {
        // Handle network errors and rollback optimistic update
        rollbackReaction(reactionType);
      }
    },
    [id, toggleReactionOnPostMutation, rollbackReaction, trackReactEvent]
  );

  // Handle API call for reply reactions
  const handleReplyReactionClick = useCallback(
    async (reactionType: PostReactionType) => {
      try {
        const { data: reactReplyData, errors: reactReplyError } =
          await toggleReactionOnReplyMutation({
            variables: {
              id,
              reactionType,
              numberOfReactions: NUMBER_OF_REACTIONS,
              multipleReactionsEnabled: true,
            },
          });

        const userErrors = reactReplyData?.toggleReactionOnReply?.userErrors;

        if (userErrors?.length || reactReplyError) {
          // Handle user errors with specific messages and rollback optimistic update
          if (userErrors?.length) {
            const errorMessage = userErrors[0]?.message;
            if (errorMessage) {
              failureToastMessage(errorMessage);
            } else {
              genericToastAlert();
            }
          }
          // Rollback the optimistic update
          rollbackReaction(reactionType);
        } else {
          // Success - track the event
          const updatedReply = reactReplyData?.toggleReactionOnReply?.reply;
          const hasReacted = updatedReply?.myReactions?.includes(reactionType);

          trackReactEvent({
            hasReacted,
            reactionType,
          });
        }
      } catch {
        // Handle network errors and rollback optimistic update
        rollbackReaction(reactionType);
      }
    },
    [id, toggleReactionOnReplyMutation, rollbackReaction, trackReactEvent]
  );

  // Main reaction handler that combines optimistic update + API call
  const handleReactionClick = useCallback(
    async (reactionType: PostReactionType) => {
      applyOptimisticReaction(reactionType);

      // Make API call in the background
      if (!parentPostId && !parentReplyId) {
        await handleParentPostReactionClick(reactionType);
      } else {
        await handleReplyReactionClick(reactionType);
      }
    },
    [
      applyOptimisticReaction,
      parentPostId,
      parentReplyId,
      handleParentPostReactionClick,
      handleReplyReactionClick,
    ]
  );

  return {
    reactionStats,
    myReactions,
    handleReactionClick,
  };
};
